import { useEffect, useMemo } from 'react';
import Select, { components } from 'react-select';
import classNames from 'classnames';
import { ENV_CONFIG } from 'config/environment';
import { getIsRecipient, getRoleDisplay } from 'config/permissions';
import { Toast } from 'components/Toast';
import Icon, { IconNames } from 'components/Icon';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { getUserData } from 'selectors/user';
import { getSelectedRecipient as getSelectedRecipientSelector } from 'selectors/admins';
import { getUserName, setRecipients, getRoleTypeOptions } from 'services/storage';
import { adminsRecipientSelected, adminsCleanUp } from 'store/admins/slice';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useGetAdminsRecipientsQuery } from 'services/adminsApi';
import colors from 'constants/colors';
import { RecipientOption, useRecipientOptions } from 'components/Forms/RecipientOptions';
import RecipientSingleValue from 'components/Forms/RecipientSingleValue';
import { resourceSelectStyles, mobileResourceSelectStyles } from 'helpers/styles';
import { useNavbarCommon } from '../../hooks/useNavbarCommon';
import type { IAdminRecipient } from 'store/admins/types';

interface RecipientNavbarProps {
  activeRole: string;
}

const RecipientDashNavbar = ({ activeRole }: RecipientNavbarProps) => {
  const {
    isAuthenticated,
    sidebarOpen,
    toggleSidebar,
    handleLogout,
    isOpen,
    toggle,
    handleChangeRoleType,
  } = useNavbarCommon();

  const dispatch = useAppDispatch();

  const userData = useAppSelector(getUserData);

  // Fetch recipients using RTK Query
  const { data: recipients = [], isLoading: isLoadingRecipients } = useGetAdminsRecipientsQuery(
    undefined,
    {
      skip: !isAuthenticated || !activeRole || !getIsRecipient(activeRole),
    }
  );

  // Use useLocalStorage hook to manage selectedRecipient with automatic localStorage sync
  const [selectedRecipient, setSelectedRecipient] = useLocalStorage<IAdminRecipient | null>(
    'selectedRecipient',
    null,
    getSelectedRecipientSelector,
    value => (value ? adminsRecipientSelected(value) : { type: 'NOOP' })
  );

  // Build rich recipient options (with logoUrl and subtitle) Select menu
  const recipientOptions = useRecipientOptions(recipients);

  const selectedRecipientOption = useMemo(() => {
    if (!selectedRecipient?.id) return null;
    return recipientOptions.find(opt => opt.value === selectedRecipient.id) || null;
  }, [selectedRecipient, recipientOptions]);

  const handleChangeRecipient = item => {
    // Find the selected recipient from the list
    const newSelectedRecipient = recipients.find(r => r.id === item.value);
    if (newSelectedRecipient) {
      if (newSelectedRecipient.id !== selectedRecipient?.id) {
        trackEvent('Recipient Switched', { recipient_id: newSelectedRecipient.id });
      }
      // Clear old OBCheck data before selecting new recipient
      dispatch(adminsCleanUp());
      // Update Redux & localStorage via useLocalStorage hook
      setSelectedRecipient(newSelectedRecipient);
    }
  };

  // Initialize recipient selection when recipients are loaded
  // useLocalStorage hook handles reading from localStorage, we just need to handle the first load
  useEffect(() => {
    if (recipients?.length > 0 && !isLoadingRecipients) {
      if (!selectedRecipient) {
        // If no recipient is selected (not in Redux or localStorage), select the first one
        setSelectedRecipient(recipients[0]);
      } else {
        // Always refresh from the API response so stale localStorage data (e.g. a field
        // that has since been removed) doesn't persist across sessions
        const freshRecipient = recipients.find(r => r.id === selectedRecipient.id);
        setSelectedRecipient(freshRecipient ?? recipients[0]);
      }
    }
  }, [recipients, selectedRecipient, isLoadingRecipients, setSelectedRecipient]);

  // Store recipients in localStorage when they're loaded (consistent with clients pattern)
  useEffect(() => {
    if (recipients && recipients.length > 0) {
      const recipientsForStorage = recipients.map(recipient => ({
        id: recipient.id,
        name: recipient.name,
        legal_name: recipient.legal_name,
        photo_id: recipient.photo_id,
      }));
      setRecipients(recipientsForStorage);
    }
  }, [recipients]);

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <Icon
          name={props.selectProps.menuIsOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
          className="chevron-svg-oath-blue"
          color={colors.white}
        />
      </components.DropdownIndicator>
    );
  };

  const NoOptionsMessage = (props: any) => {
    return <components.NoOptionsMessage {...props}>No options</components.NoOptionsMessage>;
  };

  return (
    <>
      <nav className="navbar-absolute fixed-top navbar-transparent navbar navbar-expand-lg">
        <Toast />
        <div className="container-fluid">
          <div className="navbar-wrapper">
            <div
              className={classNames('navbar-toggle', {
                toggled: sidebarOpen,
              })}
            >
              <button className="navbar-toggler" onClick={toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </button>
            </div>
            <a href="#" className="navbar-brand" onClick={e => e.preventDefault()}>
              <span className="d-none d-md-block">{userData?.client_name}</span>
            </a>
          </div>
          <div className="navbar-clients-col col-6 col-lg-3 col-md-6 col-sm-6 col-xs-6">
            <div className="navbar-select hidden-on-mobile">
              <Select
                className="navbar-recipients"
                classNamePrefix="react-select"
                name="singleSelect"
                value={selectedRecipientOption}
                onChange={handleChangeRecipient}
                options={recipientOptions}
                styles={resourceSelectStyles(selectedRecipientOption, true)}
                placeholder="Select Recipient"
                getOptionValue={opt => opt.value}
                getOptionLabel={opt => opt.label}
                components={{
                  DropdownIndicator,
                  Option: RecipientOption,
                  SingleValue: RecipientSingleValue,
                  NoOptionsMessage,
                }}
              />
            </div>
            <img
              src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/oath_logos/Oath_FullLockup_Color_LightText.svg`}
              className="logo-img-file"
              alt="Oath Logo"
            />
          </div>
          <button type="button" className="navbar-logout padding0" onClick={handleLogout}>
            Log Out
          </button>
          <div className="col-md-6 col-sm-6 col-xs-6 padding0">
            <div className="justify-content-end collapse navbar-collapse">
              <Icon
                name={IconNames.User}
                className="oath-user-svg"
                color={colors.white}
                size={20}
              />
              <div className="navbar-user">
                <span className="oath-body inkBlue">{getUserName()}</span>
                <span className="oath-utility inkBlue">{selectedRecipient?.name}</span>
              </div>
              <ul className="navbar-nav">
                <li
                  className={classNames('btn-rotate dropdown nav-item', {
                    show: isOpen,
                  })}
                >
                  <a
                    aria-haspopup={true}
                    color="default"
                    data-toggle="dropdown"
                    id="navbarDropdownMenuLink"
                    aria-expanded={isOpen}
                    onClick={toggle}
                  >
                    <Icon
                      name={isOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
                      className="chevron-svg-inkblue"
                      color={colors.white}
                    />
                  </a>
                  <div
                    role="menu"
                    aria-hidden={!isOpen}
                    className={classNames('dropdown-menu dropdown-menu-right', {
                      show: isOpen,
                    })}
                    aria-labelledby="navbarDropdownMenuLink"
                  >
                    {Array.from(getRoleTypeOptions() as string[])
                      .filter(roleType => roleType !== activeRole)
                      .map(roleType => (
                        <button
                          key={roleType}
                          onClick={() => handleChangeRoleType(roleType)}
                          type="button"
                          className="dropdown-item"
                          role="menuitem"
                        >
                          {`Change to ${getRoleDisplay(roleType)} View`}
                        </button>
                      ))}
                    <a href="#" onClick={handleLogout} className="dropdown-item" role="menuitem">
                      Log Out
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="hidden-on-desktop full-width">
          <div className="container-fluid navbar-clients-container">
            <div className="navbar-clients-col col-md-12 is-mobile">
              <div className="navbar-select">
                <Select
                  className="navbar-recipients"
                  classNamePrefix="react-select"
                  name="singleSelect"
                  value={selectedRecipientOption}
                  onChange={handleChangeRecipient}
                  options={recipientOptions}
                  styles={mobileResourceSelectStyles(selectedRecipientOption, true)}
                  placeholder="Select Recipient"
                  getOptionValue={opt => opt.value}
                  getOptionLabel={opt => opt.label}
                  components={{
                    DropdownIndicator,
                    Option: RecipientOption,
                    SingleValue: RecipientSingleValue,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default RecipientDashNavbar;
